import React from 'react';
import { Sparkles } from 'lucide-react';

type PendingAutocountComponent = { name: string; isLighting?: boolean } | null;

type AutocountDetection = {
  prediction: any;
  thumbnail: string;
  id: string;
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const getLightingThumbnailScale = (prediction: any, isLighting: boolean | undefined): number => {
  if (!isLighting) return 1;

  const relWidth = Number(prediction?.relWidth);
  const relHeight = Number(prediction?.relHeight);
  if (!Number.isFinite(relWidth) || !Number.isFinite(relHeight)) return 1.8;

  const longestSide = Math.max(relWidth, relHeight);
  if (!(longestSide > 0)) return 1.8;

  // Smaller bboxes should get more zoom so labels stay readable.
  return clamp(0.12 / Math.max(longestSide, 0.003), 1.4, 6);
};

export interface AutocountReviewModalProps {
  show: boolean;
  pendingAutocountComponent: PendingAutocountComponent;
  isAutocounting: boolean;
  autocountDetections: AutocountDetection[];
  selectedDetectionsToKeep: Set<string>;
  pageNumber: number;
  autocountTotalBatches: number;
  autocountCompletedBatches: number;
  toggleDetectionSelection: (id: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  setSelectedDetectionsToKeep: React.Dispatch<React.SetStateAction<Set<string>>>;
}

export const AutocountReviewModal: React.FC<AutocountReviewModalProps> = ({
  show,
  pendingAutocountComponent,
  isAutocounting,
  autocountDetections,
  selectedDetectionsToKeep,
  pageNumber,
  autocountTotalBatches,
  autocountCompletedBatches,
  toggleDetectionSelection,
  onConfirm,
  onCancel,
  setSelectedDetectionsToKeep,
}) => {
  if (!show || !pendingAutocountComponent || (!isAutocounting && autocountDetections.length === 0)) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100005,
        padding: '20px',
      }}
      onClick={() => {
        // Don't close on overlay click - force user to review.
      }}
    >
      <div
        style={{
          background: 'var(--bg-primary)',
          borderRadius: '16px',
          padding: '28px',
          width: '95vw',
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.4)',
          border: '1px solid var(--border-color)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ marginBottom: '20px', borderBottom: '2px solid var(--border-color)', paddingBottom: '16px' }}>
          <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '22px', fontWeight: 600, marginBottom: '8px' }}>
            Review Detections: {pendingAutocountComponent.name}
          </h3>
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '14px' }}>
            {`Found ${autocountDetections.length} detections. Click a card to approve or disapprove it. All detections are approved by default.`}
          </p>
          <div style={{ marginTop: '12px', display: 'flex', gap: '16px', fontSize: '14px' }}>
            <span style={{ color: 'var(--green-text)', fontWeight: 600 }}>
              ✓ Approved: {selectedDetectionsToKeep.size}
            </span>
            <span style={{ color: '#dc2626', fontWeight: 600 }}>
              ✗ Disapproved: {autocountDetections.length - selectedDetectionsToKeep.size}
            </span>
          </div>
        </div>

        {isAutocounting && autocountTotalBatches > 0 && (
          <div style={{ padding: '12px 16px', borderRadius: '10px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontWeight: 500, color: 'var(--text-secondary)', fontSize: '14px' }}>
                Counting...
              </span>
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                {Math.round((autocountCompletedBatches / autocountTotalBatches) * 100)}%
              </span>
            </div>

            <div style={{ width: '100%', height: '6px', background: 'var(--border-color)', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{
                width: `${(autocountCompletedBatches / autocountTotalBatches) * 100}%`,
                height: '100%',
                background: '#3b82f6',
                borderRadius: '3px',
                transition: 'width 0.3s ease-out',
              }} />
            </div>

            <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--text-muted)', textAlign: 'right' }}>
              {autocountDetections.length} detections found
            </div>
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto', marginBottom: '20px' }}>
          {autocountDetections.length === 0 && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'var(--text-muted)',
              textAlign: 'center',
              padding: '40px',
            }}>
              {isAutocounting ? (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <Sparkles size={48} color="#3b82f6" />
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                    Scanning for components...
                  </div>
                  <div style={{ fontSize: '14px', marginTop: '8px' }}>
                    Detections will appear here as they're found
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>😕</div>
                  <div style={{ fontSize: '18px', fontWeight: 500, color: 'var(--text-secondary)' }}>
                    No components found
                  </div>
                  <div style={{ fontSize: '14px', marginTop: '8px' }}>
                    Try selecting a different reference area or adjusting the component type
                  </div>
                </>
              )}
            </div>
          )}

          {autocountDetections.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: '16px',
              padding: '4px',
            }}>
              {autocountDetections.map((det, idx) => {
                const isSelected = selectedDetectionsToKeep.has(det.id);
                const detPage = det.prediction.page || pageNumber;
                const thumbnailScale = getLightingThumbnailScale(
                  det.prediction,
                  pendingAutocountComponent.isLighting
                );
                return (
                  <div
                    key={det.id}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleDetectionSelection(det.id);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.02)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = isSelected ? '0 0 12px rgba(59, 130, 246, 0.5)' : 'none';
                    }}
                    style={{
                      border: isSelected ? '3px solid #3b82f6' : '3px solid var(--border-color)',
                      borderRadius: '10px',
                      padding: '8px',
                      cursor: 'pointer',
                      background: isSelected ? 'var(--bg-primary)' : 'var(--bg-tertiary)',
                      transition: 'all 0.2s',
                      opacity: isSelected ? 1 : 0.6,
                      position: 'relative',
                      boxShadow: isSelected ? '0 0 12px rgba(59, 130, 246, 0.5), 0 0 24px rgba(59, 130, 246, 0.3)' : 'none',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                    }}
                  >
                    {!isSelected && (
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        background: '#dc2626',
                        color: 'white',
                        borderRadius: '50%',
                        width: '28px',
                        height: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                      }}>
                        ✗
                      </div>
                    )}

                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      left: '8px',
                      background: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: 500,
                    }}>
                      P{detPage}
                    </div>

                    <div style={{
                      overflow: 'hidden',
                      borderRadius: '6px',
                      marginBottom: '8px',
                      aspectRatio: '4/3',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <img
                        src={det.thumbnail}
                        alt={`Detection ${idx + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'center',
                          transform: `scale(${thumbnailScale})`,
                          transformOrigin: 'center center',
                          background: 'var(--bg-primary)',
                          pointerEvents: 'none',
                        }}
                      />
                    </div>

                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center' }}>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                        #{idx + 1}
                      </div>
                      <div style={{ fontSize: '11px', marginTop: '2px' }}>
                        Confidence: {(det.prediction.confidence * 100).toFixed(0)}%
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', borderTop: '2px solid var(--border-color)', paddingTop: '16px' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => {
                setSelectedDetectionsToKeep(new Set(autocountDetections.map(d => d.id)));
              }}
              style={{
                padding: '10px 16px',
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border-strong)',
                borderRadius: '8px',
                color: 'var(--text-secondary)',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Select All
            </button>
            <button
              onClick={() => {
                setSelectedDetectionsToKeep(new Set());
              }}
              style={{
                padding: '10px 16px',
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border-strong)',
                borderRadius: '8px',
                color: 'var(--text-secondary)',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Clear All
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {selectedDetectionsToKeep.size > 0 && (
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', marginRight: '4px' }}>
                Total Detections: <span style={{ color: 'var(--text-primary)' }}>{selectedDetectionsToKeep.size}</span> {selectedDetectionsToKeep.size === 1 ? 'credit' : ''}
              </span>
            )}
            <button
              onClick={onCancel}
              style={{
                padding: '12px 20px',
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border-strong)',
                borderRadius: '8px',
                color: 'var(--text-secondary)',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={selectedDetectionsToKeep.size === 0 || isAutocounting}
              style={{
                padding: '12px 24px',
                background: (selectedDetectionsToKeep.size > 0 && !isAutocounting) ? '#3b82f6' : 'var(--border-strong)',
                border: 'none',
                borderRadius: '8px',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: 500,
                cursor: (selectedDetectionsToKeep.size > 0 && !isAutocounting) ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
              onMouseEnter={(e) => {
                if (selectedDetectionsToKeep.size > 0 && !isAutocounting) {
                  e.currentTarget.style.background = '#2563eb';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedDetectionsToKeep.size > 0 && !isAutocounting) {
                  e.currentTarget.style.background = '#3b82f6';
                }
              }}
            >
              {isAutocounting ? 'Counting...' : `Confirm ${selectedDetectionsToKeep.size > 0 ? `(${selectedDetectionsToKeep.size})` : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
